import React, { type FC, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { LoadingButton } from '@mui/lab'
import { TextField } from '@mui/material'
import { useFormik } from 'formik'
import { object, string, type InferType } from 'yup'
import { useAppDispatch, useAppSelector } from 'store/hooks'
import { StoreStatus } from 'common/utils/types'
import styles from 'common/navigation/auth-layout/AuthLayout.module.scss'

import { forgotPassword } from '../auth.service'
import { resetStatus, resetMessageAndStatus } from '../auth.slice'
import Message from '../Message'

const forgotPasswordSchema = object({
  email: string().email('Invalid email').required('Required field').default(''),
})

export type ForgotPasswordForm = InferType<typeof forgotPasswordSchema>

const ForgotPassword: FC = () => {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const { forgotPasswordStatus, status } = useAppSelector((state) => state.auth)

  const defaultValues: ForgotPasswordForm = {
    email: '',
  }

  useEffect(() => {
    dispatch(resetMessageAndStatus())
  }, [dispatch])

  useEffect(() => {
    if (forgotPasswordStatus === StoreStatus.Succeeded) {
      navigate('/auth/reset-password', { state: formik.values })
      dispatch(resetStatus())
    }
  }, [dispatch, navigate, forgotPasswordStatus])

  const formik = useFormik({
    initialValues: defaultValues,
    validationSchema: forgotPasswordSchema,
    onSubmit: async (values: ForgotPasswordForm) => await dispatch(forgotPassword(values)),
  })

  return (
    <div className={styles.container}>
      <h2 className="text-center">Zaboravljena lozinka</h2>

      <Message />

      <form onSubmit={formik.handleSubmit} className={styles.formContainer}>
        <TextField
          id="email"
          name="email"
          label="Email"
          placeholder="name@example.com"
          type="email"
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          value={formik.values.email}
          error={formik.touched.email && !!formik.errors.email}
          helperText={formik.touched.email && formik.errors.email}
          sx={{
            width: '100%',
          }}
          InputProps={{
            classes: {
              root: styles.input,
            },
          }}
        />

        <LoadingButton type="submit" loading={status === StoreStatus.Loading} variant="outlined">
          Resetuj lozinku
        </LoadingButton>
      </form>
    </div>
  )
}

export default ForgotPassword
